import { Ref, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VdsBasicDropdownMultiselect, VdsDropdownItem } from '@volt/volt-extended-components-react';
import { VdsBasicDropdownMultiselectCustomEvent } from '@volt/volt-basic-components';
import { getDropdownItemStringValue, getDropdownItemValue } from '../dropdown-items/utils';
import { DropdownItemProps } from '../dropdown-items/DropdownItems';

interface DropdownMultiselectProps<T> {
  uid: string;
  // TODO: After upgrading to React 19, replace elementRef with ref since forwardRef is no longer required.
  // https://react.dev/reference/react/forwardRef
  elementRef?: Ref<HTMLVdsBasicDropdownMultiselectElement>;
  value: T[];
  items: DropdownItemProps<T>[];
  onChange: (values: T[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  displaySelectionLimit?: number;
  displaySelectionLimitText?: string;
  maxHeight?: string;
  showClearButton?: boolean;
}

export const DropdownMultiselect = <T,>({
  uid,
  elementRef,
  value,
  items,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  displaySelectionLimit = 4,
  displaySelectionLimitText,
  maxHeight = '300px',
  showClearButton = true,
}: DropdownMultiselectProps<T>) => {
  const { t } = useTranslation();

  /**
   * Unlike the onValueChanged prop of the VdsBasicDropdown, this prop is only called when the user selection changes
   * and not when the clear button or clear() method is used.
   */
  const onValueChanged = useCallback(
    ({ detail }: VdsBasicDropdownMultiselectCustomEvent<string[]>) => {
      const values: T[] = [];

      for (let i = 0; i < detail.length; i++) {
        // look up the values using the dropdown string value
        const value: T | undefined = getDropdownItemValue(items, detail[i]);

        if (typeof value !== 'undefined') {
          values.push(value);
        } else {
          // In theory, this error should not be reached.
          console.error(
            'An event was triggered that contains a vds item value that does not match the item source list.',
          );
        }
      }

      onChange(values);
    },
    [onChange, items],
  );

  const onCleared = useCallback(() => {
    // Send an empty array to the store when the input is cleared by the user.
    onChange([]);
  }, [onChange]);

  /**
   * FIXME: Passing an array to the value prop breaks the component on the FIRST mount.
   * Previously, it used to break on the SECOND+ mount: https://gitlab.corp.wabtec.com/volt/volt/-/issues/1763
   * Workaround: Turn the array of values into a comma delimited string.
   */
  const controlValue = useMemo<string>(() => {
    const stringValues: string[] = [];

    for (let i = 0; i < value.length; i++) {
      // look up the values using the dropdown string value
      const stringValue: string | undefined = getDropdownItemStringValue(items, value[i]);

      if (typeof stringValue !== 'undefined') {
        stringValues.push(stringValue);
      } else {
        // Todo: Make sure this code cannot be reached.
        console.error('One of the provided values could not be found in the items list.');
      }
    }

    return stringValues.join(',');
  }, [items, value]);

  // If no matching initial value could be found amongst the provided items, return without rendering.
  if (typeof value === 'undefined') {
    return null;
  }

  return (
    <VdsBasicDropdownMultiselect
      ref={elementRef}
      id={uid}
      /**
       * The VdsBasicDropdownMultiselect 'value' prop appears to be partially decoupled from state.
       * The 'value' prop acts more like an initial value that gets ignored after the first render.
       * However, if the clear() method (not the clear button) is used to clear the selection, the 'value' prop
       * is then used to determine the selection after the component's internal state has been cleared.
       */
      value={controlValue}
      onValueChanged={onValueChanged}
      onCleared={onCleared}
      onBlur={onBlur}
      placeholder={placeholder ?? t('ShowAll')}
      disabled={disabled}
      showClearButton={showClearButton}
      /**
       * VDS does not treat the value 0 the same as other integers, meaning that it is not possible to show the
       * displaySelectionLimitText when one item is selected. For values of 0, using a decimal value between
       * 0 and 1 bypasses the VDS internal logic that interprets 0 as a falsey value.
       */
      displaySelectionLimit={displaySelectionLimit <= 0 ? 0.1 : displaySelectionLimit}
      displaySelectionLimitText={displaySelectionLimitText ?? t('items selected')}
      size="medium"
      maxHeight={maxHeight}
      matchWidth
    >
      {items.map(({ key, stringValue, label }: DropdownItemProps<T>) => {
        return (
          <VdsDropdownItem
            key={key}
            value={stringValue}
            label={label}
            size="medium"
            hasCheckmark
            checkmarkVariant="checkbox"
          />
        );
      })}
    </VdsBasicDropdownMultiselect>
  );
};

export default DropdownMultiselect;
